import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Switch from 'react-switch';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { e4Format } from 'helpers/dates';

import { ArrayHelpers, Loading, Modal, SystemText, DebugInfo } from 'e4shared';

import PageHeader from 'components/general/pageHeader';
import SortFilter from 'components/general/sortFilter';
import UserMenu from 'components/user/menu';
import EditAppraisal from './components/editAppraisal';
import PDF from './components/pdf';
import Wizard from './components/wizard';
import { apiGet, apiPatch, apiPost } from 'services';

import './appraisals.css';
import NoResults from 'components/general/noResults';
import { setShowSuccess } from 'actions/ui';
import { getPermissions } from 'helpers/permissions';
import Schedule from '../reviewTracker/components/schedule';
import { translateAppraisalJson } from 'helpers/translations';
import posthog from 'posthog-js';

export const ReviewState = (appraisals, activeAppraisal, user, profile, wizard = false, showPast = false) => {
    const tempState = {};
    const app = { ...activeAppraisal, participants: activeAppraisal?.participants || 'UM' };
    tempState.wizard = wizard;
    tempState.user = user.id === app.profile_Id || user.id === profile.id;
    tempState.includeManager = app.participants.indexOf('M') >= 0;
    tempState.includeUser = app.participants.indexOf('U') >= 0;
    tempState.manager = user.id === app.manager_Id || (!app.profile_Id && user.id === profile.manager_Id);
    tempState.coach = app.coach_Id == user.id || (!app.profile_Id && user.id === profile.coach_Id);
    tempState.appraisalManager = user.id === app.manager_Id;
    tempState.userManager = user.id === profile.manager_Id;
    tempState.invalidManager =
        !app.submission && !app.coach_Id && tempState.includeManager && profile.manager_Id !== app.manager_Id;
    tempState.appraisalCoach = user.id === app.coach_Id;
    tempState.userCoach = user.id === profile.coach_Id;
    tempState.invalidCoach =
        !app.submission && tempState.coach && tempState.includeManager && profile.coach_Id !== app.coach_Id;

    tempState.neverActive = !ArrayHelpers.existsAndHasLength(appraisals);
    tempState.notActive = !app.profile_Id;
    tempState.active = app.profile_Id && !app.submission;
    tempState.started =
        app.profile_Id &&
        ((tempState.user && app.user_Predictions) || ((tempState.manager || tempState.coach) && app.manager_Predictions));
    tempState.completed =
        app.profile_Id &&
        ((tempState.user && app.user_Comments_Done) ||
            ((tempState.manager || tempState.coach) && app.manager_Comments_Done));
    tempState.finalReady = app.user_Comments_Done && app.manager_Comments_Done;
    tempState.finalStarted = app.started;
    tempState.past = app.submission || showPast;
    tempState.readonly = tempState.past || tempState.completed || tempState.finalStarted;
    tempState.inProgress = tempState.finalStarted && !tempState.past && (tempState.manager || tempState.coach);
    return tempState;
};

export const UserAppraisals = ({ history, match }) => {
    const dispatch = useDispatch();
    const { set } = queryString.parse(history.location.search);
    const id = match.params.id;
    const { coaching, downline, user } = useSelector(state => state.user);

    const [activeAppraisal, setActiveAppraisal] = React.useState(null);
    const [appraisals, setAppraisals] = React.useState(null);
    const [congrats, setCongrats] = React.useState(false);
    const [inReview, setInReview] = React.useState(false);
    const [pastAppraisals, setPastAppraisals] = React.useState(null);
    const [pastIndex, setPastIndex] = React.useState(0);
    const [predictions, setPredictions] = React.useState(null);
    const [permissions, setPermissions] = React.useState(getPermissions());
    const [profile, setProfile] = React.useState({});
    const [showPast, setShowPast] = React.useState(false);
    const [showReset, setShowReset] = React.useState(false);
    const [showSchedule, setShowSchedule] = React.useState(set);
    const [stage, setStage] = React.useState(-1);
    const [state, setState] = React.useState({});

    DebugInfo('component', 'UserAppraisals');

    if (user && !match.params.id) history.replace(match.path.replace(':id?', user.id));

    React.useEffect(() => {
        if (showPast) setCongrats(false);
    }, [showPast]);

    const api = {
        loadAppraisals: callback =>
            apiGet(
                `appraisals/${id}`,
                user.guid,
                response => {
                    setAppraisals(response);
                    callback?.(response);
                },
                () => history.replace('/error')
            ),
        loadProfile: () =>
            apiGet(
                `profile/${id}`,
                user.guid,
                response => setProfile(response),
                () => history.replace('/error')
            ),
        startAppraisal: () => {
            apiPatch(`appraisal-start/${id}`, activeAppraisal, user.guid, response => {
                posthog.capture('capability-review-started');
                api.loadAppraisals();
                setCongrats(false);
            });
        },
        finaliseAppraisal: (app, callback) =>
            apiPatch(`appraisal-finalise`, app, user.guid, () => {
                posthog.capture('capability-review-completion');
                dispatch(setShowSuccess(SystemText('Reviews/Appraisals/Finalise/Success')));
                api.loadAppraisals();
                callback?.();
            }),
        unlock: (app, callback) =>
            apiPatch(`appraisal-unlock`, app, user.guid, () => {
                dispatch(setShowSuccess(SystemText('Reviews/Appraisals/Unlock/Success')));

                const temp = { ...activeAppraisal };
                temp.missing = false;
                if (state.user) temp.user_Comments_Done = null;
                if (state.manager || state.coach) temp.manager_Comments_Done = null;
                setActiveAppraisal(temp);

                callback?.();
            }),
        updateAppraisal: (app, completed) => {
            const temp = { ...activeAppraisal };

            if (state.user) {
                temp.user_Predictions = app.user_Predictions;
                temp.user_Competency_Comments = app.user_Competency_Comments;
                temp.user_Comments = app.user_Comments;
                temp.user_Comments_Done = app.user_Comments_Done;
            } else {
                temp.manager_Predictions = app.manager_Predictions;
                temp.manager_Competency_Comments = app.manager_Competency_Comments;
                temp.manager_Comments = app.manager_Comments;
                temp.manager_Comments_Done = app.manager_Comments_Done;
            }

            temp.goals = app.goals;

            setActiveAppraisal(temp);

            apiPatch(
                'appraisal-comment',
                app,
                user.guid,
                () => {
                    completed && setCongrats(true);
                },
                result => {
                    if (result.response.status === 409) {
                        history.replace('/develop/reviews/'); // load the main reviews page an let the standard logic take over
                    }
                }
            );
        },
        assign: (details, callback) => {
            apiPost('review-assign', details, user.guid, () => {
                dispatch(setShowSuccess(SystemText('ReviewTracker/Tabs/Reviews/Success')));
                setShowSchedule(false);
                callback();
            });
        },
        unassignReschedule: (app, callback) => {
            apiPatch('review-unassign-reschedule', app, user.guid, () => {
                dispatch(setShowSuccess(SystemText('ReviewTracker/Tabs/Reschedule/Success')));
                api.loadAppraisals();
            });
        },
    };

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            setPermissions(getPermissions(profile, user, coaching, downline));
        }
    }, [profile?.id, user?.id]);

    const initialise = reset => {
        setShowPast(false);
        if (reset) {
            setStage(-1);
            setActiveAppraisal(null);
            setState({});
        }
        setPredictions([]);
        api.loadProfile();
        api.loadAppraisals();
    };

    React.useEffect(() => {
        if (id) {
            initialise();
        }
    }, [id]);

    // Set Active and Past appraisals
    React.useEffect(() => {
        if (appraisals) {
            setActiveAppraisal(appraisals.find(a => !a.submission));
            setPastAppraisals(appraisals.filter(a => a.submission).sort((a, b) => (a.submission > b.submission ? -1 : 1)));
        }
    }, [appraisals]);

    // Set State
    React.useEffect(() => {
        if (user?.id && profile?.id) {
            const tempState = ReviewState(appraisals, activeAppraisal, user, profile, false, showPast);
            setState(tempState);
        }
    }, [activeAppraisal, profile?.id, user?.id]);

    // Set Predictions
    React.useEffect(() => {
        if (activeAppraisal && profile.id) {
            let rawPredictions = state.user ? activeAppraisal.user_Predictions : activeAppraisal.manager_Predictions;
            if (rawPredictions === '') rawPredictions = null;
            setPredictions(predictions ? JSON.parse(rawPredictions) : []);
            if (showPast && pastAppraisals) {
                setStage(activeAppraisal.competencies.length);
            } else if (activeAppraisal && state.completed && !state.finalStarted) {
                setStage(activeAppraisal.competencies.length);
            } else if (activeAppraisal && state.finalStarted && !state.past) {
                setStage(activeAppraisal.competencies.length);
            }
        }
    }, [activeAppraisal, profile.id, state]);

    const setAgreedRatingValue = (id, level) => {
        const ratings = JSON.parse(activeAppraisal.ratings || '[]');
        const rating = ratings.find(r => r.id === id);
        if (rating) {
            rating.level = level;
        } else {
            ratings.push({ id, level });
        }

        const app = { ...activeAppraisal, ratings: JSON.stringify(ratings) };
        apiPatch(`appraisal-ratings`, app, user.guid, () => {
            setActiveAppraisal(app);
        });
    };

    React.useEffect(() => {
        if (activeAppraisal?.missing) {
            setShowReset(true);
        }
    }, [activeAppraisal?.missing]);

    React.useEffect(() => {
        if (showPast) {
            apiGet(`appraisal-json/${pastAppraisals[pastIndex].id}`, user.guid, json => {
                const temp = { ...pastAppraisals[pastIndex] };
                temp.json = JSON.stringify(json);
                temp.competencies = json.competencies || [];
                translateAppraisalJson(user.language_Code, temp);
                setActiveAppraisal(temp);
            });
        } else if (appraisals) {
            setStage(-1);
            api.loadAppraisals(appraisals => {
                setActiveAppraisal(appraisals.find(a => !a.submission));
            });
        }
    }, [showPast, pastIndex]);

    const finalise = (appraisal, callback) => {
        const manager = activeAppraisal.manager_Predictions ? JSON.parse(activeAppraisal.manager_Predictions) : [];
        const staff = activeAppraisal.user_Predictions ? JSON.parse(activeAppraisal.user_Predictions) : [];

        const competencies = appraisal.competencies.map(c => ({
            ...c,
            manager: manager.find(x => x.competency_Id === c.id)?.level,
            staff: staff.find(x => x.competency_Id === c.id)?.level,
        }));

        appraisal.json = confirm
            ? JSON.stringify({ language_Id: user.language_Id, appraisal, competencies, profile })
            : undefined;

        api.finaliseAppraisal(appraisal, callback);
    };

    const showScheduleButton =
        !showPast &&
        ((activeAppraisal && permissions.reviews.canEditSchedule) || (!activeAppraisal && permissions.reviews.canSchedule));

    const toggle = () =>
        ArrayHelpers.existsAndHasLength(pastAppraisals) ? (
            <label className={showPast ? 'switch-on' : 'switch-off'}>
                <span>{SystemText('Reviews/Toggle/ShowPast')}</span>
                <Switch className="react-switch" onChange={() => setShowPast(!showPast)} checked={showPast} />
            </label>
        ) : null;

    const wizard = profile.id && (
        <Wizard
            appraisal={activeAppraisal}
            congrats={congrats}
            finalise={finalise}
            finalising={state.finalStarted && !state.past}
            inReview={inReview}
            permissions={permissions}
            profile={profile}
            setAgreedRatingValue={setAgreedRatingValue}
            setCongrats={setCongrats}
            setInReview={setInReview}
            setStage={s => {
                setStage(s);
                setCongrats(false);
            }}
            showBothManagerAndStaffRatingInfo={(state.finalStarted || state.past) && activeAppraisal?.participants === 'UM'}
            stage={stage}
            state={state}
            updateAppraisal={api.updateAppraisal}
            unassignReschedule={api.unassignReschedule}
        />
    );

    const extraButtons = () => {
        const temp = [];

        const scheduleButton = (
            <button
                key="appraisal-buttons-schedule"
                data-testid="appraisal-buttons-schedule"
                className="btn btn-success"
                title={
                    (!profile.manager_Id && !activeAppraisal) || !ArrayHelpers.existsAndHasLength(profile.competency_Ids)
                        ? SystemText('Reviews/Buttons/Schedule/Disabled')
                        : ''
                }
                disabled={
                    (!profile.manager_Id && !activeAppraisal) || !ArrayHelpers.existsAndHasLength(profile.competency_Ids)
                }
                onClick={() => {
                    setShowSchedule(true);
                }}
            >
                <i className="fa fa-calendar mr10" />
                {SystemText(`Reviews/Buttons/${activeAppraisal ? 'Manage' : 'Schedule'}`)}
            </button>
        );

        const userButton = <UserMenu key="appraisal-buttons-usermenu" profile={profile} user={user} hide={'appraisals'} />;

        const pdfButton = activeAppraisal ? <PDF key="appraisal-buttons-pdf" appraisal={activeAppraisal} /> : null;

        showPast && temp.push(pdfButton);
        showScheduleButton && temp.push(scheduleButton);
        temp.push(userButton);

        return temp;
    };

    return (
        <div id="appraisals">
            {user && (
                <PageHeader
                    overrideProfile={profile ? profile : null}
                    title={SystemText('Reviews/Title')}
                    extra={() => <div className="flex">{extraButtons()}</div>}
                />
            )}
            <section className="content mtm10" data-testid="appraisal-content">
                <SortFilter toggle={toggle} />
                {!appraisals || !permissions.reviews.canView || profile.id?.toString() !== match.params.id ? (
                    <Loading />
                ) : (
                    <div>
                        {showPast && (
                            <div className="row mb10 mtm10">
                                <div className="col-md-12">
                                    <label htmlFor="select-area" className="mt10">
                                        {SystemText('Reviews/Jump/Label')}:
                                    </label>
                                    <select
                                        id="select-appraisal"
                                        className="form-control input-lg"
                                        onChange={e => setPastIndex(e.target.value)}
                                        value={pastIndex}
                                    >
                                        {pastAppraisals.map((a, i) => {
                                            return (
                                                <option key={`app_${a.id}`} value={i}>
                                                    {SystemText('Reviews/Appraisals/Scheduled', e4Format(a.scheduled))}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        )}
                        {!state.active && !showPast && (
                            <div className="row">
                                <div className="col-md-12 mb10 text-center" data-testid="text-noresults">
                                    <NoResults
                                        title={SystemText('Reviews/NoActive/Title')}
                                        message={SystemText(
                                            `Reviews/${state.neverActive ? 'Never' : 'NoActive'}/${
                                                state.user ? 'You' : 'Them'
                                            }`,
                                            profile.first_Name
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                        {activeAppraisal &&
                            ((state.manager && !activeAppraisal.coach_Id) || (state.coach && activeAppraisal.coach_Id)) &&
                            activeAppraisal.participants === 'UM' &&
                            user.reviewViaSitdown &&
                            state.manager &&
                            state.finalReady &&
                            !state.past &&
                            !state.finalStarted && (
                                <div className="row">
                                    <div className="col-md-12 mb10 flex flex-center">
                                        <button
                                            data-testid="button-begin"
                                            className="btn btn-success mr10"
                                            disabled={activeAppraisal.missing || activeAppraisal.missingOther}
                                            onClick={() => api.startAppraisal()}
                                        >
                                            <i className="fa fa-universal-access mr5" />
                                            {SystemText('Reviews/Appraisals/Begin')}
                                        </button>
                                        {!activeAppraisal.missingOther && (
                                            <span className="flex-grow">
                                                <i className="fa fa-warning text-orange mr5" />
                                                {SystemText('Reviews/Appraisals/Begin/Warning')}
                                            </span>
                                        )}
                                    </div>
                                    {activeAppraisal.missingOther && (
                                        <div className="col-md-12 mb10">
                                            <div
                                                className="flex flex-center bg-red"
                                                style={{ color: '#fff', borderRadius: '2px', padding: '10px' }}
                                            >
                                                <i className="fa fa-warning fa-lg mr10" />
                                                <span className="flex-grow">
                                                    {SystemText('Reviews/Appraisals/Begin/Missing', profile.first_Name)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        {activeAppraisal && (
                            <div className="row">
                                {state.past ||
                                state.invalidManager ||
                                state.invalidCoach ||
                                (permissions.reviews.canComplete(activeAppraisal) &&
                                    ((state.includeUser && state.user) || (state.includeManager && state.manager))) ? (
                                    <div className="col-md-12">
                                        <div>{wizard}</div>
                                    </div>
                                ) : (
                                    <div className="col-md-12 mb10 text-center">
                                        <NoResults
                                            title={SystemText('Reviews/Ongoing/Title', profile.full_Name)}
                                            message={SystemText('Reviews/Ongoing/Message')}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </section>
            <Modal
                show={showReset}
                title={SystemText('Reviews/Appraisals/Unlock/Title')}
                close={() => setShowReset(false)}
                footer={
                    <div>
                        <button
                            onClick={() => api.unlock(activeAppraisal, () => setShowReset(false))}
                            className="btn btn-success"
                        >
                            {SystemText('Reviews/Appraisals/Unlock/Button')}
                        </button>
                        <button onClick={() => setShowReset(false)} className="btn">
                            {SystemText('Reviews/Appraisals/Unlock/Cancel')}
                        </button>
                    </div>
                }
            >
                {state.user
                    ? SystemText('Reviews/Appraisals/Unlock/Description/You')
                    : SystemText('Reviews/Appraisals/Unlock/Description/Them', profile.first_Name)}
            </Modal>
            <Schedule
                show={showSchedule && !activeAppraisal ? [profile.id] : null}
                users={[profile]}
                close={() => setShowSchedule(false)}
                onSchedule={app => api.assign(app, api.loadAppraisals)}
            />
            {activeAppraisal && (
                <EditAppraisal
                    user={user}
                    permissions={permissions}
                    coaching={coaching}
                    loadAppraisals={api.loadAppraisals}
                    profile={profile}
                    show={showSchedule && activeAppraisal?.id}
                    appraisal={activeAppraisal}
                    reload={reset => {
                        initialise(reset);
                    }}
                    close={begin => {
                        setShowSchedule(null);
                        begin && setInReview(false);
                        begin && setStage(-1);
                    }}
                />
            )}
        </div>
    );
};

export default withRouter(UserAppraisals);
